import { useEffect, useState } from 'react'
import {
  ApiError,
  getEvent,
  getLocations,
  signUp,
  type EventCard,
  type LocationOption,
} from './api'
import { SignupForm } from './SignupForm'
import { ResidentPage } from './resident/ResidentPage'
import { ResourcePage } from './resource/ResourcePage'
import { ActivitiesByCode, ActivitiesByQuery } from './activities/ActivitiesPage'

/** uuid события берём из пути `/e/<uuid>` — роутер ради одного экрана не нужен. */
function eventUuidFromPath(): string | null {
  const match = window.location.pathname.match(/\/e\/([\w-]+)/)
  return match ? match[1] : null
}

function formatWhen(event: EventCard): string {
  const tz = event.room.location.timezone
  const date = new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    timeZone: tz,
  }).format(new Date(event.startTime))
  const time = (iso: string) =>
    new Intl.DateTimeFormat('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: tz,
    }).format(new Date(iso))
  return `${date}, ${time(event.startTime)}–${time(event.endTime)}`
}

export function App() {
  // Страница резидента — та же сборка, своя ветка по пути (роутер ради двух
  // экранов не нужен). Витрины ставят на неё ссылку «Я резидент».
  if (window.location.pathname.startsWith('/resident')) return <ResidentPage />
  const resourceId = window.location.pathname.match(/^\/r\/(\d+)/)?.[1]
  if (resourceId) return <ResourcePage resourceId={Number(resourceId)} />
  if (/^\/a\/?$/.test(window.location.pathname)) return <ActivitiesByQuery />
  // Код сборной страницы — буквы и цифры (rooms/public_code на бэке).
  const pageCode = window.location.pathname.match(/^\/a\/([A-Za-z0-9]+)\/?$/)?.[1]
  if (pageCode) return <ActivitiesByCode code={pageCode} />
  return <EventPage />
}

function EventPage() {
  const uuid = eventUuidFromPath()
  const [event, setEvent] = useState<EventCard | null>(null)
  const [locations, setLocations] = useState<LocationOption[]>([])
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  useEffect(() => {
    if (!uuid) {
      setError('Ссылка неполная — в ней нет кода мероприятия')
      return
    }
    Promise.all([getEvent(uuid), getLocations()])
      .then(([card, locs]) => {
        setEvent(card)
        setLocations(locs)
      })
      .catch((e: unknown) =>
        setError(
          e instanceof ApiError ? e.message : 'Мероприятие не найдено',
        ),
      )
  }, [uuid])

  if (error) {
    return (
      <main className="card">
        <h1>Не получилось</h1>
        <p className="muted">{error}</p>
      </main>
    )
  }

  if (!event) {
    return (
      <main className="card">
        <p className="muted">Загружаем…</p>
      </main>
    )
  }

  const soldOut = event.seatsLeft !== null && event.seatsLeft <= 0
  const past = new Date(event.endTime) <= new Date()

  return (
    <main className="card">
      <h1>{event.title}</h1>
      <p className="when">{formatWhen(event)}</p>
      <p className="muted">
        {event.room.location.name}
        {event.room.location.address ? `, ${event.room.location.address}` : ''}
        {' · '}
        {event.room.name}
      </p>
      {event.description && <p className="description">{event.description}</p>}

      {event.seatsLeft !== null && !past && (
        <p className={soldOut ? 'seats seats--out' : 'seats'}>
          {soldOut ? 'Мест не осталось' : `Свободно мест: ${event.seatsLeft}`}
        </p>
      )}

      {done ? (
        <p className="done">Вы записаны. До встречи!</p>
      ) : past ? (
        <p className="muted">Мероприятие уже прошло</p>
      ) : soldOut ? null : (
        <SignupForm
          locations={locations}
          onSubmit={async (payload) => {
            await signUp(event.uuid, payload)
            setDone(true)
            setEvent(await getEvent(event.uuid))
          }}
        />
      )}
    </main>
  )
}
